import { useCallback, useState } from 'react'
import { OrganizationDetails } from '@/features/organization/api/OrganizationModel'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import EmailVerificationPage from './EmailVerificationPage'
import OrganizationDetailsPage from './OrganizationDetailsPage'
import VerificationPendingPage from './VerificationPendingPage'
import { useSession } from '@/shared/hooks/useSession'
import { useSaveOrganizationMandatoryInfo } from '@/shared/hooks/api/organizations'
import OnboardingLayout from './OnboardingLayout'

export default function OnboardingFlow() {
  const { session } = useSession()
  const { organization } = session

  const isMandatoryInfoComplete = organization.isMandatoryInfoComplete

  const [emailVerificationState, setEmailVerificationState] = useState({
    verificationCode: ''
  })

  const [state, setState] = useState<OrganizationDetails>({
    placeOfRegistration: '',
    organizationName: '',
    localName: ''
    // businessRegistrationNumber: '' // TODO: add back later
  })

  const navigate = useNavigate()

  const onEmailVerificationComplete = useCallback(
    ({ verificationCode }: { verificationCode: string }) => {
      setEmailVerificationState({
        verificationCode
      })
      navigate('../organization-details')
    },
    [navigate]
  )

  const { mutateAsync: saveOrganizationMandatoryInfo } = useSaveOrganizationMandatoryInfo(
    session.organization.id
  )

  const onOrganizationDetailsComplete = useCallback(
    async (payload: OrganizationDetails) => {
      const newState: OrganizationDetails = {
        ...state,
        ...payload
      }
      setState(newState)

      try {
        await saveOrganizationMandatoryInfo(newState)
        navigate('../success')
      } catch (error) {
        console.error(error)
      }
    },
    [state, saveOrganizationMandatoryInfo, navigate]
  )

  const isDoneWithEmailVerification = session?.isEmailVerified

  const isDoneWithOnboarding = isMandatoryInfoComplete && isDoneWithEmailVerification

  function getRedirectRoute() {
    if (isDoneWithOnboarding) {
      return 'success'
    }

    if (isDoneWithEmailVerification) {
      return 'organization-details'
    }

    return 'email-verification'
  }

  const redirectRoute = getRedirectRoute()

  return (
    <Routes>
      <Route element={<OnboardingLayout state={state} />}>
        <Route
          path="email-verification"
          element={
            isDoneWithEmailVerification ? (
              <Navigate to={`/onboarding/${redirectRoute}`} />
            ) : (
              <EmailVerificationPage
                initialState={emailVerificationState}
                next={onEmailVerificationComplete}
              />
            )
          }
        />
        <Route
          path="organization-details"
          element={
            isDoneWithOnboarding ? (
              <Navigate to={`/onboarding/${redirectRoute}`} />
            ) : (
              <OrganizationDetailsPage next={onOrganizationDetailsComplete} initialState={state} />
            )
          }
        />
        <Route path="success" element={<VerificationPendingPage />} />
        <Route index element={<Navigate to={`/onboarding/${redirectRoute}`} />} />
      </Route>
    </Routes>
  )
}
